import React from 'react';
import { SportEventSelectionItem } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createPictureFactory, Picture } from '@funtech-int/yango-shared';
import { useInView } from 'react-intersection-observer';

import * as CardKit from '@/shared/components/CardKit';
import { useI18n } from '@/shared/hooks/useI18n';

import { TeamsEventMeta } from '../TeamsEventMeta/TeamsEventMeta';
import { StageBadge } from '../StageBadge/StageBadge';
import { PromoProgressBar } from '../PromoProgressBar/PromoProgressBar';

import styles from './PromoEventPreview.module.css';

interface PromoEventPreviewProps {
    item: SportEventSelectionItem;
    currentTimestamp: number;
    onShow: () => void;
}

const posterFactory = createPictureFactory({
    fallback: {
        src: '655x368',
    },
});

export const PromoEventPreview: React.FC<PromoEventPreviewProps> = (props) => {
    const { item, currentTimestamp, onShow } = props;
    const i18n = useI18n();

    const { ref: inViewRef } = useInView({
        onChange(inView: boolean) {
            if (inView) {
                onShow();
            }
        },
        threshold: 0.8,
    });

    const eventState = item.getState(currentTimestamp);
    const { posterUrl } = item.getAssets();
    const poster = posterUrl ? posterFactory({ fallbackUrl: posterUrl }) : undefined;
    const isPurchased = Boolean(item.viewOption?.isPurchased);
    const isLive = eventState === 'LIVE';

    return (
        <div className={styles.root} ref={inViewRef}>
            {poster ? (
                <Picture src={poster.img.src} className={styles.poster} alt={item.title} />
            ) : null}
            {!isPurchased && item.viewOption?.subscriptionBadge && (
                <div className={styles.purchaseBadge}>
                    <CardKit.PurchaseBadge
                        ariaLabel={i18n('card__purchasable')}
                        badge={item.viewOption.subscriptionBadge}
                    />
                </div>
            )}
            <div className={styles.stageBadgeContainer}>
                <StageBadge
                    eventState={eventState}
                    startTime={item.startTime}
                    endTime={item.endTime}
                    flat
                />
            </div>
            {item.typename === 'TeamsSportEvent' ? (
                <TeamsEventMeta className={styles.teamsEventMeta} sportEvent={item} size="s" />
            ) : (
                <span className={styles.previewMetaEventTitle}>{item.title}</span>
            )}

            {isLive && (
                <div className={styles.progressBarWrapper}>
                    <PromoProgressBar
                        currentTimestamp={currentTimestamp}
                        startTime={item.startTime}
                        endTime={item.endTime}
                    />
                </div>
            )}
        </div>
    );
};
